import { type BrowseListSelection } from '@/pages/browse/delete-items';
import { cleanKeys, restrictionQuery } from '@/pages/browse/delete-items/deleteItemsSelection';

type Translate = (
    key: string,
    interpolationValues?: Record<string, string | number>,
) => string;

type BrowseToolbarRow = Record<string, unknown> & {
    primaryKey__?: string;
    category__?: string;
};

type BrowseToolbarSelection = Omit<BrowseListSelection, 'selectedItems'> & {
    selectedItems: BrowseToolbarRow[];
    /** Current browse query, including pagination keys. */
    itemsQuery?: Record<string, unknown>;
    routeCategory: string | null;
    /** Watchlist `selectionId` from `$routeParams`. */
    routeSelectionId?: unknown;
    pauseEditing?: () => void;
};

type ToolbarItemSelection =
    | { primaryKeys: string[] }
    | { itemsQuery: Record<string, unknown> };

const selectedPrimaryKeys = (selection: Pick<BrowseToolbarSelection, 'selectedItems'>): string[] => (
    cleanKeys(selection.selectedItems.map((item) => (
        typeof item.primaryKey__ === 'string' ? item.primaryKey__ : ''
    )))
);

const hasToolbarSelection = (
    selection: Pick<BrowseToolbarSelection, 'allItemsSelected' | 'selectedItems'>,
): boolean => selection.allItemsSelected || selectedPrimaryKeys(selection).length > 0;

/**
 * Angular `createItemRestrictions()`: a watchlist route restricts to its
 * selection, otherwise the search filters without pagination keys.
 */
const itemRestrictions = (
    selection: Pick<BrowseToolbarSelection, 'itemsQuery' | 'routeSelectionId'>,
): Record<string, unknown> => {
    const { routeSelectionId } = selection;
    if (routeSelectionId != null && routeSelectionId !== '') {
        return { selectionId: routeSelectionId };
    }
    return restrictionQuery(selection.itemsQuery) ?? {};
};

/** Global select-all sends the restrictions, otherwise the checked keys. */
const toolbarItemSelection = (selection: BrowseToolbarSelection): ToolbarItemSelection => (
    selection.allItemsSelected
        ? { itemsQuery: itemRestrictions(selection) }
        : { primaryKeys: selectedPrimaryKeys(selection) }
);

/** `checkItemPermission` warning from `items-controller.js`. */
const itemPermissionWarning = (t: Translate, action: string, item: BrowseToolbarRow): string => {
    if (item.primaryKey__) {
        return t('ITEM_ACTION.FORBIDDEN_FOR_PRIMARY_KEY', { action, primaryKey: item.primaryKey__ });
    }
    if (item.category__) {
        return t('ITEM_ACTION.FORBIDDEN_FOR_CATEGORY', { action, category: item.category__ });
    }
    return t('ITEM_ACTION.FORBIDDEN', { action });
};

const categoryContext = (routeCategory: string | null) => (
    { category__: routeCategory } as unknown as Record<string, string>
);

export type { BrowseToolbarRow, BrowseToolbarSelection, ToolbarItemSelection };
export {
    categoryContext,
    hasToolbarSelection,
    itemPermissionWarning,
    itemRestrictions,
    selectedPrimaryKeys,
    toolbarItemSelection,
};
